import { Button, Drawer, Group, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors } from '@styles/design-system';
import { type FC, useEffect, useState } from 'react';

import TraceView from './TraceView';

/**
 * A batch's history, opened from the shelf it sits on.
 *
 * THE TRACE PAGE, WITHOUT LEAVING THE TAB. The person is standing in Finished Goods looking at
 * a batch; its history belongs beside it, not on a screen that asks them to type the code
 * again. The Trace page keeps its search for "I have a code on a drum and nothing else".
 *
 * A BACK STACK RATHER THAN A URL. On the page each hop rewrites the query string so an
 * investigation is one shareable link; in a drawer that would hijack the tab's own URL. The
 * stack is the same idea kept local: pressing Trace on a linked batch pushes, Back pops, and
 * closing forgets the lot.
 *
 * NEVER NESTED. Both batch panels are drawers too; the host closes them and opens this one, so
 * there is one drawer on screen at a time and the back button means one thing.
 */
const TraceDrawer: FC<{
    /** The batch to open on, or null to close. */
    batchId: string | null;
    /** A manufacturer sees only what passed through its own site; the server enforces it. */
    asSite: boolean;
    onClose: () => void;
}> = ({ batchId, asSite, onClose }) => {
    const [stack, setStack] = useState<string[]>([]);

    // A new opening starts a new investigation; the previous one's hops are not its business.
    useEffect(() => {
        setStack(batchId ? [batchId] : []);
    }, [batchId]);

    const current = stack[stack.length - 1] ?? null;

    return (
        <Drawer
            opened={!!batchId}
            onClose={onClose}
            position="right"
            size="xl"
            title={
                <Group gap={8}>
                    {stack.length > 1 && (
                        <Button
                            size="xs"
                            variant={ButtonVariant.SUBTLE}
                            onClick={() => setStack((prev) => prev.slice(0, -1))}
                        >
                            Back
                        </Button>
                    )}
                    <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                        History
                    </Text>
                </Group>
            }
        >
            {current && (
                <TraceView
                    batchId={current}
                    asSite={asSite}
                    onTraceBatch={(next) => setStack((prev) => [...prev, next])}
                />
            )}
        </Drawer>
    );
};

export default TraceDrawer;
